import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { HELOC_CUSTOMER_SERVICE_DISPLAY, HELOC_CUSTOMER_SERVICE_TEL } from '@ld.consumer-portal/constants';
import type { AddBankAccountRequest, AddBankAccountResponse, PaymentProfile, RoutingNumberValidation } from '@ld.consumer-portal/models';
import { errorIcon, sampleCheckDiagram } from '@ld.consumer-portal/data-uri';
import { NumericOnlyDirective } from '@ld.consumer-portal/utils';
import { PaymentElectronicCommunicationsDisclosureComponent } from '../payment-electronic-communications-disclosure/payment-electronic-communications-disclosure.component';
import { TermsAndConditionsModalComponent } from '../terms-and-conditions-modal/terms-and-conditions-modal.component';

export interface AddBankAccountDialogData {
  borrowerId: string;
  loanNumber: string;
  encryptionValue: string;
  loanType?: string;
  prefillFirstName?: string;
  prefillLastName?: string;
  disableNameFields?: boolean;
  hideAccountOwnerTypeToggle?: boolean;
  validateRoutingNumber: (
    routingNumber: string,
    loanNumber: string,
    encryptionValue: string,
  ) => Promise<RoutingNumberValidation>;
  addBankAccount: (
    borrowerId: string,
    request: AddBankAccountRequest,
    loanNumber: string,
    encryptionValue: string,
  ) => Promise<AddBankAccountResponse>;
}

export type AddBankAccountDialogResult =
  | { action: 'cancel' }
  | { action: 'added'; profile: PaymentProfile };

@Component({
  selector: 'ld-add-bank-account-dialog',
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    NumericOnlyDirective,
    TranslocoDirective,
    PaymentElectronicCommunicationsDisclosureComponent,
    TermsAndConditionsModalComponent,
  ],
  templateUrl: './add-bank-account-dialog.component.html',
  styleUrl: './add-bank-account-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AddBankAccountDialogComponent {
  private readonly dialogRef = inject<DialogRef<AddBankAccountDialogResult>>(DialogRef);
  protected readonly data = inject<AddBankAccountDialogData>(DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly transloco = inject(TranslocoService);

  protected readonly checkDiagramSrc = sampleCheckDiagram;
  protected readonly errorIcon = errorIcon;

  protected readonly helocPhone = HELOC_CUSTOMER_SERVICE_DISPLAY;
  protected readonly helocTel = HELOC_CUSTOMER_SERVICE_TEL;

  protected readonly isHeloc = computed(() => this.data.loanType === '1');

  protected readonly faqLink = computed(() => `/help/${this.data.loanNumber}/faqs`);

  protected readonly isLoading = signal(false);

  protected readonly isValidatingRouting = signal(false);

  protected readonly showTermsModal = signal(false);

  protected readonly showElectronicCommsModal = signal(false);

  protected readonly bankName = signal<string>('');

  protected readonly isRoutingValidated = signal(false);

  protected readonly errorMessage = signal<string>('');

  protected readonly ffcSelection = signal<'yes' | 'no' | null>(null);

  protected readonly showFfcErrorModal = signal(false);

  protected readonly showRoutingErrorModal = signal(false);

  protected readonly showAccountValidationErrorModal = signal(false);

  private accountValidationAttempts = 0;

  private routingValidationAttempts = 0;

  private static readonly MAX_ROUTING_ATTEMPTS = 3;

  private static readonly ALPHANUMERIC_PATTERN = /^[a-zA-Z0-9 ]*$/;

  private static readonly NAME_MAX_LENGTH = 100;

  protected readonly bankAccountForm = this.fb.group({
    accountNickname: [
      '',
      [
        Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
        Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
      ],
    ],
    accountOwnerType: ['personal' as 'personal' | 'business', [Validators.required]],
    accountType: ['checking' as 'checking' | 'savings', [Validators.required]],
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
        Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
      ],
    ],
    lastName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
        Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
      ],
    ],
    businessName: ['', []],
    routingNumber: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    accountNumber: ['', [Validators.required, Validators.pattern(/^\d{4,17}$/)]],
    confirmAccountNumber: ['', [Validators.required]],
    saveAccount: [true],
    makeDefault: [true],
  });

  private readonly prefillNameEffect = effect(() => {
    const firstName = this.data.prefillFirstName;
    const lastName = this.data.prefillLastName;
    const disable = this.data.disableNameFields;

    if (firstName) {
      this.bankAccountForm.get('firstName')?.setValue(firstName);
    }
    if (lastName) {
      this.bankAccountForm.get('lastName')?.setValue(lastName);
    }
    if (disable) {
      this.bankAccountForm.get('firstName')?.disable();
      this.bankAccountForm.get('lastName')?.disable();
    }
  });

  protected get isFormValid(): boolean {
    const form = this.bankAccountForm;
    const accountNumber = form.get('accountNumber')?.value;
    const confirmAccountNumber = form.get('confirmAccountNumber')?.value;
    const isPersonal = form.get('accountOwnerType')?.value === 'personal';

    const hasRequiredName = isPersonal
      ? !!(
          (form.get('firstName')?.valid || form.get('firstName')?.disabled) &&
          (form.get('lastName')?.valid || form.get('lastName')?.disabled)
        )
      : (form.get('businessName')?.value?.trim().length ?? 0) > 0;

    const ffcAllowed = !this.isHeloc() || this.ffcSelection() === 'no';

    return !!(
      ffcAllowed &&
      form.get('accountOwnerType')?.valid &&
      form.get('accountType')?.valid &&
      form.get('routingNumber')?.valid &&
      form.get('accountNumber')?.valid &&
      accountNumber === confirmAccountNumber &&
      hasRequiredName &&
      this.isRoutingValidated()
    );
  }

  protected onClose(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  protected onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  protected async onRoutingNumberBlur(): Promise<void> {
    const routingNumber = this.bankAccountForm.get('routingNumber')?.value;

    if (!routingNumber || routingNumber.length !== 9) {
      this.isRoutingValidated.set(false);
      this.bankName.set('');
      return;
    }

    this.isValidatingRouting.set(true);
    this.errorMessage.set('');

    try {
      const result = await this.data.validateRoutingNumber(
        routingNumber,
        this.data.loanNumber,
        this.data.encryptionValue,
      );

      if (result.isValid) {
        this.isRoutingValidated.set(true);
        this.bankName.set(result.bankName ?? '');
        this.routingValidationAttempts = 0;
      } else {
        this.isRoutingValidated.set(false);
        this.bankName.set('');
        this.routingValidationAttempts++;

        if (this.routingValidationAttempts >= AddBankAccountDialogComponent.MAX_ROUTING_ATTEMPTS) {
          this.showRoutingErrorModal.set(true);
        } else {
          this.bankAccountForm.get('routingNumber')?.setErrors({ invalidRouting: true });
          this.errorMessage.set(this.transloco.translate('errorsRoutingNumberNotFound'));
        }
      }
    } catch {
      this.isRoutingValidated.set(false);
      this.bankName.set('');
      this.errorMessage.set(this.transloco.translate('errorsRoutingValidationFailed'));
    } finally {
      this.isValidatingRouting.set(false);
    }
  }

  protected onRoutingNumberInput(): void {
    this.isRoutingValidated.set(false);
    this.bankName.set('');
  }

  protected onAccountNumberInput(): void {
    this.errorMessage.set('');
    this.validateAccountNumberMatch();
  }

  protected onConfirmAccountNumberInput(): void {
    this.validateAccountNumberMatch();
  }

  private validateAccountNumberMatch(): void {
    const confirmControl = this.bankAccountForm.get('confirmAccountNumber');
    const accountNumber = this.bankAccountForm.get('accountNumber')?.value;
    const confirmAccountNumber = confirmControl?.value;

    if (confirmControl?.touched && confirmAccountNumber && accountNumber !== confirmAccountNumber) {
      confirmControl.setErrors({ mismatch: true });
    } else if (confirmControl?.hasError('mismatch')) {
      confirmControl.setErrors(null);
    }
  }

  protected onSaveAccountChange(): void {
    const saveAccount = this.bankAccountForm.get('saveAccount')?.value;
    if (!saveAccount) {
      this.bankAccountForm.get('makeDefault')?.setValue(false);
    }
  }

  protected onFfcChange(value: 'yes' | 'no'): void {
    this.ffcSelection.set(value);
    if (value === 'yes') {
      this.showFfcErrorModal.set(true);
    }
  }

  protected closeFfcErrorModal(): void {
    this.showFfcErrorModal.set(false);
  }

  protected onAccountOwnerTypeChange(): void {
    const isPersonal = this.bankAccountForm.get('accountOwnerType')?.value === 'personal';

    if (isPersonal) {
      this.bankAccountForm.get('businessName')?.clearValidators();
      this.bankAccountForm.get('businessName')?.setValue('');
      this.bankAccountForm
        .get('firstName')
        ?.setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
          Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
        ]);
      this.bankAccountForm
        .get('lastName')
        ?.setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
          Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
        ]);
    } else {
      this.bankAccountForm.get('firstName')?.clearValidators();
      this.bankAccountForm.get('firstName')?.setValue('');
      this.bankAccountForm.get('lastName')?.clearValidators();
      this.bankAccountForm.get('lastName')?.setValue('');
      this.bankAccountForm
        .get('businessName')
        ?.setValidators([
          Validators.required,
          Validators.maxLength(AddBankAccountDialogComponent.NAME_MAX_LENGTH),
          Validators.pattern(AddBankAccountDialogComponent.ALPHANUMERIC_PATTERN),
        ]);
    }

    this.bankAccountForm.get('firstName')?.updateValueAndValidity();
    this.bankAccountForm.get('lastName')?.updateValueAndValidity();
    this.bankAccountForm.get('businessName')?.updateValueAndValidity();
  }

  protected async onAddAccount(): Promise<void> {
    if (!this.isFormValid) {
      this.bankAccountForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    const formValue = this.bankAccountForm.getRawValue();
    const isPersonal = formValue.accountOwnerType === 'personal';

    const request: AddBankAccountRequest = {
      profileName: formValue.accountNickname || undefined,
      profileType: 'BankAccount',
      defaultPaymentProfile: formValue.makeDefault ?? true,
      saveAccountInformation: formValue.saveAccount ?? true,
      paymentProfileDetails: {
        firstName: isPersonal ? (formValue.firstName?.trim() ?? '') : '',
        lastName: isPersonal ? (formValue.lastName?.trim() ?? '') : '',
        businessAccountName: !isPersonal ? formValue.businessName?.trim() : undefined,
        accountProfile: isPersonal ? 'Personal' : 'Business',
        accountType: formValue.accountType === 'checking' ? 'Checking' : 'Savings',
        routingNumber: formValue.routingNumber ?? '',
        accountNumber: formValue.accountNumber ?? '',
      },
      channelId: 2, // Servicing Portal
      loanType: this.data.loanType,
      userId: this.data.borrowerId,
      retriesExhausted: this.accountValidationAttempts >= 2,
    };

    try {
      const response = await this.data.addBankAccount(
        this.data.borrowerId,
        request,
        this.data.loanNumber,
        this.data.encryptionValue,
      );

      if (response.accountValidationResult && !response.accountValidationResult.valid) {
        this.accountValidationAttempts++;

        if (this.accountValidationAttempts >= 3) {
          this.errorMessage.set('');
          this.showAccountValidationErrorModal.set(true);
        } else {
          this.errorMessage.set(this.transloco.translate('errorsAccountValidationFailed'));
        }
      } else if (response.accountVerificationStatus === 'Valid' && response.profile) {
        if (!response.profile.bankName && this.bankName()) {
          response.profile.bankName = this.bankName();
        }
        this.dialogRef.close({ action: 'added', profile: response.profile });
      } else if (response.accountVerificationStatus === 'Invalid') {
        this.accountValidationAttempts++;

        if (this.accountValidationAttempts >= 3) {
          this.errorMessage.set('');
          this.showAccountValidationErrorModal.set(true);
        } else {
          this.errorMessage.set(
            response.errorMessage
              ? this.transloco.translate(response.errorMessage)
              : this.transloco.translate('errorsAccountVerificationFailed'),
          );
        }
      } else if (response.accountVerificationStatus === 'Error') {
        this.dialogRef.close({ action: 'cancel' });
      } else {
        this.errorMessage.set(
          response.errorMessage
            ? this.transloco.translate(response.errorMessage)
            : this.transloco.translate('commonAnErrorOccurredPleaseTryAgain'),
        );
      }
    } catch {
      this.errorMessage.set(this.transloco.translate('errorsAddBankAccountFailed'));
    } finally {
      this.isLoading.set(false);
    }
  }

  protected openTermsModal(event: Event): void {
    event.preventDefault();
    this.showTermsModal.set(true);
  }

  protected closeTermsModal(): void {
    this.showTermsModal.set(false);
  }

  protected openElectronicCommsModal(event: Event): void {
    event.preventDefault();
    this.showElectronicCommsModal.set(true);
  }

  protected closeElectronicCommsModal(): void {
    this.showElectronicCommsModal.set(false);
  }

  protected closeAccountValidationErrorModal(): void {
    this.showAccountValidationErrorModal.set(false);
    this.dialogRef.close({ action: 'cancel' });
  }
}
